import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  Tooltip,
  Typography,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import StopIcon from '@mui/icons-material/Stop';
import { apiFetch } from '../../auth/apiFetch';

const apiBaseUrl = process.env.REACT_APP_ECS_CONTROLLER_URL || window.location.origin;
const POLL_INTERVAL_MS = 5000;

/**
 * AppBar widget for manually starting and stopping the ECS backend service.
 *
 * When ECS is stopped, /classify and other heavy endpoints return 503.
 * The lightweight /docx and /health routes (served by Lambda) always work.
 */
function EcsControl() {
  const [status, setStatus] = useState(null); // null | 'stopped' | 'starting' | 'running'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch(`${apiBaseUrl}/api/admin/ecs/status`);
      if (!res.ok) return;
      const data = await res.json();
      const { desiredCount, runningCount } = data;
      if (desiredCount === 0) {
        setStatus('stopped');
      } else if (runningCount > 0) {
        setStatus('running');
      } else {
        setStatus('starting');
      }
      setError(null);
    } catch {
      // Silently ignore — ECS controller may not be deployed yet
    }
  }, []);

  // Poll while starting (every 5s); poll less often when stable (every 30s)
  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const handleStart = async () => {
    setLoading(true);
    try {
      await apiFetch(`${apiBaseUrl}/api/admin/ecs/start`, { method: 'POST' });
      setStatus('starting');
      setError(null);
    } catch {
      setError('Failed to start');
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    setLoading(true);
    try {
      await apiFetch(`${apiBaseUrl}/api/admin/ecs/stop`, { method: 'POST' });
      setStatus('stopped');
      setError(null);
    } catch {
      setError('Failed to stop');
    } finally {
      setLoading(false);
    }
  };

  if (status === null) return null; // ECS controller not deployed yet — hide widget

  const statusColor = status === 'running' ? 'success' : status === 'starting' ? 'warning' : 'default';
  const statusLabel = status === 'running' ? 'Server: online' : status === 'starting' ? 'Server: starting…' : 'Server: offline';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
      <Tooltip title={status === 'stopped' ? 'Server is offline. Click Start to wake up.' : 'Server is running.'}>
        <Chip
          label={statusLabel}
          color={statusColor}
          size="small"
          icon={status === 'starting' ? <CircularProgress size={12} color="inherit" /> : undefined}
          sx={{ color: 'white', borderColor: 'white', '.MuiChip-icon': { color: 'white' } }}
          variant="outlined"
        />
      </Tooltip>

      {status === 'stopped' && (
        <Tooltip title="Start server (~90s boot time)">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <PowerSettingsNewIcon />}
              onClick={handleStart}
              disabled={loading}
              sx={{ whiteSpace: 'nowrap', minWidth: 0 }}
            >
              Start
            </Button>
          </span>
        </Tooltip>
      )}

      {(status === 'running' || status === 'starting') && (
        <Tooltip title="Stop server (auto-stops after 30 min idle)">
          <span>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <StopIcon />}
              onClick={handleStop}
              disabled={loading || status === 'starting'}
              sx={{ whiteSpace: 'nowrap', minWidth: 0 }}
            >
              Stop
            </Button>
          </span>
        </Tooltip>
      )}

      {error && (
        <Typography variant="caption" color="error" sx={{ color: '#ffcdd2' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default EcsControl;
